import React from 'react';

import { CheckboxWithLabel } from '@depop/web-ui-kit/CheckboxWithLabel';
import { SortIcon } from '@depop/web-ui-kit/Icons/SortIcon';
import clsx from 'clsx';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';

import searchFilterStyles from '../baseFilterStyles.module.css';

import styles from './styles.module.css';

import { ButtonMinimal } from '@/components/ButtonMinimal';
import { Dropdown } from '@/components/Dropdown';
import { SortByValues, type SortBy } from '@/modules/search/types';
import { useSearchFilters } from '@/modules/search/useSearchFilters';

type Props = {
  className?: string;
  options: Array<{
    id: SortBy;
    name: string;
  }>;
};

export function SortByFilter({ className, options }: Props) {
  const {
    searchFilters: { sort },
    setSearchFilters: { setSortFilter },
  } = useSearchFilters();
  const t = useTranslations('common');
  const pathname = usePathname();

  function handleSortChange(value: SortBy) {
    if (value === SortByValues.SellersRecommendations) {
      // If no filter has been applied and the user clicks "Relevance", then we should not add the sort query param to the URL.
      // In this scenario, the "Relevance" option will order the products by the seller's manual sort order.
      return window.history.pushState(null, '', pathname);
    }
    return setSortFilter(value);
  }

  return (
    <Dropdown
      className={clsx(styles.sortByFilterTriggerContainer, className)}
      TriggerComponent={({ onClick }) => (
        <ButtonMinimal
          className={clsx(styles.sortByFilterTrigger)}
          onClick={onClick}
        >
          <SortIcon className={styles.sortIcon} />
          {t('Sorting.Sort')}
        </ButtonMinimal>
      )}
    >
      {({ className }) => (
        <div className={clsx(className, styles.sortByFilterDropdownContents)}>
          {options.map(({ id, name }, index) => {
            const isChecked = sort ? sort === id : index === 0;
            return (
              <CheckboxWithLabel
                className={clsx(
                  searchFilterStyles.checkboxFilterDropdownOption
                )}
                style={
                  {
                    '--font-weight': isChecked ? 'bold' : 'normal',
                  } as React.CSSProperties
                }
                key={id}
                label={t(name)}
                align="left"
                checked={isChecked}
                onChange={() => handleSortChange(id)}
              />
            );
          })}
        </div>
      )}
    </Dropdown>
  );
}
